import axios, { AxiosInstance } from 'axios'
import {TemplateComponent} from '../types/templates'
import { CustomColumnDefinition, ColumnConfigItem, LeadsTableConfig } from '../types/lead'

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1'

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
})

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const adminToken = localStorage.getItem('admin_access_token')
    const agencyToken = localStorage.getItem('access_token')
    const url = config.url || ''
    const isSelfRoute = url.startsWith('/agencies/self')
    const isAdminRoute = !isSelfRoute && (url.startsWith('/agencies') || url.startsWith('/admin'))

    // Block write requests for demo account
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        const isDemo = user.email === 'demo@lyftr.ai'
        const method = config.method?.toLowerCase() || ''
        const isWrite = ['post', 'put', 'patch', 'delete'].includes(method) || url.includes('/integrations/shopify/install')
        const isAuthRoute = url.includes('/auth/')

        if (isDemo && isWrite && !isAuthRoute) {
          console.warn(`[API] Blocked demo user write request: ${method.toUpperCase()} ${url}`)
          const errorMsg = 'This action is disabled in the Live Demo. Book an onboarding call to unlock the full experience.'
          return Promise.reject({
            response: {
              status: 403,
              data: {
                detail: errorMsg
              }
            },
            message: errorMsg
          })
        }
      } catch (err) {
        console.error('[API] Demo check error:', err)
      }
    }

    if (isAdminRoute) {
      if (adminToken) {
        config.headers.Authorization = `Bearer ${adminToken}`
      } else {
        console.warn(`[API] Missing admin_access_token for admin route: ${url}`)
      }
    } else if (agencyToken) {
      config.headers.Authorization = `Bearer ${agencyToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if we're not already on the login page
      if (!window.location.pathname.includes('/login')) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('user')
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password })
      return response.data
    } catch (error) {
      console.error('API: Login error:', error)
      throw error
    }
  },

  signup: async (name: string, email: string, password: string) => {
    const response = await api.post('/auth/signup', {
      name,
      email,
      password
    })
    return response.data
  },

  logout: async () => {
    const response = await api.post('/auth/logout')
    localStorage.removeItem('access_token')
    localStorage.removeItem('admin_access_token')
    return response.data
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me')
    return response.data
  },

  refreshToken: async () => {
    const response = await api.post('/auth/refresh')
    return response.data
  },
}

// Dashboard API
export const dashboardApi = {
  getDashboardData: async () => {
    const response = await api.get('/dashboard')
    return response.data
  },

  getMetrics: async () => {
    const response = await api.get('/dashboard/metrics')
    return response.data
  },

  getFunnelData: async (period: string = '30d') => {
    const response = await api.get('/dashboard/funnel-data', {
      params: { period }
    })
    return response.data
  },

  getAgencyMetrics: async () => {
    const response = await api.get('/dashboard/agency')
    return response.data
  },

  getSMBs: async (search?: string, statusFilter?: string) => {
    const response = await api.get('/dashboard/smbs', {
      params: { search, status_filter: statusFilter }
    })
    return response.data
  },
}

// SMBs API
export interface SMBData {
  id?: string
  name: string
  display_name?: string
  document_original_path?: string | null
  document_name?: string | null
  business_type: string
  industry: string
  owner_name: string
  owner_email: string
  phone: string
  whatsapp_phone?: string
  email?: string
  website?: string
  description?: string
  status?: string
  crawl_depth?: number | null
  crawl_pages?: number | null
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  timezone?: string
  currency?: string
  created_at?: string
  updated_at?: string
  onboarding_completed?: boolean
  metrics?: {
    total_leads: number
    qualified_leads: number
    total_bookings: number
    conversion_rate: number
    revenue_30d: number
  }
  whatsapp_business_account_id?: string | null
  whatsapp_business_portfolio_id?: string | null
  scrape_metadata?: WebsiteScrapeMetadata | null
}

export interface ScrapedItemData {
  title?: string | null
  description?: string | null
  price?: string | null
  duration?: string | null
  cta?: string | null
  source_url?: string | null
}

export interface ContentBlockData {
  block_type: string
  title?: string | null
  body: string
  language_code?: string | null
  items?: ScrapedItemData[] | null
  source_url?: string | null
}

export interface FAQData {
  question: string
  answer: string
  category?: string | null
  language_code?: string | null
}

export interface SocialLinkData {
  platform: string
  url: string
  handle?: string | null
  source?: string | null
}

export interface WebsiteScrapeMetadata {
  last_run_at?: string | null
  last_status?: string | null
  cooldown_expires_at?: string | null
}

export interface SMBDetailData extends SMBData {
  website_sections: ContentBlockData[]
  faqs: FAQData[]
  social_links: SocialLinkData[]
  scrape_metadata?: WebsiteScrapeMetadata | null
  business_hours: string[]
}

export interface WebsiteScrapeResponse {
  status?: string
  message?: string
  cooldown_expires_at?: string
  last_run_at?: string | null
  last_status?: string | null
  last_error?: string | null
  last_result?: Record<string, any> | null
}

export interface MetaEmbeddedSignupCompletePayload {
  code: string
  event: string
  waba_id?: string
  phone_number_id?: string
  business_id?: string
  smb_id?: string
}

export interface MetaEmbeddedSignupCompleteResponse {
  smb: SMBData
}

export const metaEmbeddedSignupApi = {
  completeSignup: async (
    payload: MetaEmbeddedSignupCompletePayload
  ): Promise<MetaEmbeddedSignupCompleteResponse> => {
    const response = await api.post('/integrations/meta/embedded-signup/complete', payload)
    return response.data
  },
}

export const smbsApi = {
  // Get all SMBs for the current agency
  getSMBs: async (params?: {
    search?: string
    status?: string
    page?: number
    limit?: number
    per_page?: number
    agency_id?: string
  }) => {
    const response = await api.get('/smbs/', { params })
    return response.data
  },

  // create or update SMB (multipart/form-data only; optional PDF file)
  createUpdateSMB: async (
    smbData: Record<string, any>,
    opts?: { file?: File; onUploadProgress?: (progressEvent: any) => void }
  ) => {
    const formData = new FormData()
    Object.entries(smbData || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      formData.append(key, String(value))
    })
    if (opts?.file) {
      formData.append('file', opts.file)
    }

    const response = await api.post('/smbs/', formData, {
      onUploadProgress: opts?.onUploadProgress,
    })
    return response.data
  },

  // Get single SMB details
  getSMB: async (smbId: string): Promise<SMBDetailData> => {
    const response = await api.get(`/smbs/${smbId}`)
    return response.data
  },

  // Delete SMB
  deleteSMB: async (smbId: string) => {
    await api.delete(`/smbs/${smbId}`)
  },

  // Get SMB metrics and analytics
  getSMBMetrics: async (smbId: string, period: string = '30d') => {
    const response = await api.get(`/admin/smbs/${smbId}/metrics`, {
      params: { period }
    })
    return response.data
  },

  // Toggle SMB status (activate/deactivate)
  toggleSMBStatus: async (smbId: string) => {
    const response = await api.post(`/admin/smbs/${smbId}/toggle-status`)
    return response.data
  },

  // Shopify Config
  getShopifyConfig: async (smbId: string) => {
    const response = await api.get(`/smbs/${smbId}/shopify-config`)
    return response.data
  },

  updateShopifyConfig: async (
    smbId: string,
    payload: {
      abandoned_checkout_is_enabled: boolean
      abandoned_checkout_delay_minutes: number
      abandoned_checkout_template_id: string | null
      is_order_confirmation_enabled: boolean
      order_confirmation_template_id: string | null
    }
  ) => {
    const response = await api.put(`/smbs/${smbId}/shopify-config`, payload)
    return response.data
  },

  getAiRepliesCount: async (smbId: string, startDate?: string, endDate?: string) => {
    const params: Record<string, string> = {}
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    const response = await api.get(`/smbs/${smbId}/ai-replies-count`, { params })
    return response.data
  },

  getSMBDashboardSummary: async (smbId: string) => {
    const end = new Date()
    const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000)
    const response = await api.get<SMBDashboardSummary>(
      `/smbs/${smbId}/dashboard-summary?start_date=${start.toISOString()}&end_date=${end.toISOString()}`
    )
    return response.data
  },

  getCustomColumns: async (smbId: string): Promise<{ columns: CustomColumnDefinition[] }> => {
    const response = await api.get(`/smbs/${smbId}/custom-columns`)
    return response.data
  },

  createCustomColumn: async (
    smbId: string,
    payload: { name: string; type: string; options?: string[]; key?: string }
  ): Promise<{ columns: CustomColumnDefinition[] }> => {
    const response = await api.post(`/smbs/${smbId}/custom-columns`, payload)
    return response.data
  },

  updateCustomColumns: async (
    smbId: string,
    columns: CustomColumnDefinition[]
  ): Promise<{ columns: CustomColumnDefinition[] }> => {
    const response = await api.put(`/smbs/${smbId}/custom-columns`, { columns })
    return response.data
  },

  deleteCustomColumn: async (
    smbId: string,
    columnKey: string
  ): Promise<{ columns: CustomColumnDefinition[] }> => {
    const response = await api.delete(`/smbs/${smbId}/custom-columns/${columnKey}`)
    return response.data
  },

  patchCustomColumn: async (
    smbId: string,
    columnKey: string,
    payload: { name?: string; options?: string[] }
  ): Promise<{ message: string; column: CustomColumnDefinition; columns: CustomColumnDefinition[] }> => {
    const response = await api.patch(`/smbs/${smbId}/custom-columns/${columnKey}`, payload)
    return response.data
  },

  getLeadsTableConfig: async (smbId: string): Promise<LeadsTableConfig> => {
    const response = await api.get(`/smbs/${smbId}/leads-table-config`)
    return response.data
  },

  updateLeadsTableConfig: async (
    smbId: string,
    columns: ColumnConfigItem[]
  ): Promise<LeadsTableConfig> => {
    const response = await api.put(`/smbs/${smbId}/leads-table-config`, { columns })
    return response.data
  },
}

export type LeadSourceItem = { source: string; count: number }

export type AppointmentSummaryItem = {
  slot_start: string | null
  slot_end: string | null
  user_name: string | null
  user_phone: string | null
  services: string[] | null
}

export type ConversationStats = {
  active: number
  inactive: number
  qualified: number
}

export type SMBDashboardSummary = {
  start_date: string
  end_date: string
  total_leads: number
  total_bookings: number
  total_campaigns: number
  ai_messages_sent: number
  conversation_stats: ConversationStats
  lead_sources: LeadSourceItem[]
  upcoming_appointments: AppointmentSummaryItem[]
}


export type { LeadStatusesResponse } from './leadsApi'
export { leadsApi } from './leadsApi'

// Conversations API
export const conversationsApi = {
  getConversations: async (params?: {
    page?: number
    limit?: number
    status?: string
    channel?: string
    unread_only?: boolean
    smb_id?: string
    search?: string
    sort_unread_first?: boolean
  }) => {
    const response = await api.get('/conversations/', { params })
    return response.data
  },

  getConversation: async (conversationId: string, smbId?: string) => {
    const params = smbId ? { smb_id: smbId } : {}
    const response = await api.get(`/conversations/${conversationId}`, { params })
    return response.data
  },

  updateConversationStatus: async (conversationId: string, status: string) => {
    const response = await api.put(`/conversations/${conversationId}/status`, { status })
    return response.data
  },

  markConversationRead: async (conversationId: string) => {
    const response = await api.post(`/conversations/${conversationId}/mark-read`)
    return response.data
  },

  getConversationsSummary: async (smbId?: string) => {
    const params = smbId ? { smb_id: smbId } : {}
    const response = await api.get('/conversations/stats/summary', { params })
    return response.data
  },

toggleAutoReply: async (conversationId: string, enabled: boolean) => {
    const response = await api.patch(
      `/conversations/${conversationId}/auto-reply`,
      JSON.stringify(enabled),
      { headers: { 'Content-Type': 'application/json' } }
    )
    return response.data
  },
}

// Messages API
export const messagesApi = {
  sendMessage: async (payload: any) => {
    const response = await api.post('/messages/send', payload)
    return response.data
  },
}

// Flows API
export const flowsApi = {
  getFlows: async (params?: {
    page?: number
    limit?: number
    status?: string
    trigger?: string
    active_only?: boolean
  }) => {
    const response = await api.get('/flows/', { params })
    return response.data
  },

  createFlow: async (flowData: {
    name: string
    description?: string
    trigger: string
    is_active?: boolean
  }) => {
    const response = await api.post('/flows/', flowData)
    return response.data
  },

  getFlow: async (flowId: string) => {
    const response = await api.get(`/flows/${flowId}`)
    return response.data
  },

  updateFlow: async (flowId: string, updates: {
    name?: string
    description?: string
    trigger?: string
    is_active?: boolean
  }) => {
    const response = await api.put(`/flows/${flowId}`, updates)
    return response.data
  },

  deleteFlow: async (flowId: string) => {
    const response = await api.delete(`/flows/${flowId}`)
    return response.data
  },

  activateFlow: async (flowId: string) => {
    const response = await api.post(`/flows/${flowId}/activate`)
    return response.data
  },

  deactivateFlow: async (flowId: string) => {
    const response = await api.post(`/flows/${flowId}/deactivate`)
    return response.data
  },

  getFlowsSummary: async () => {
    const response = await api.get('/flows/stats/summary')
    return response.data
  },
}

// AI API
export const aiApi = {
  draftReply: async (payload: {
    smb_id: string
    conversation_id?: string
    message: string
    history?: Array<{ role: string; content: string }>
  }) => {
    const response = await api.post('/ai/draft-reply', payload)
    return response.data
  },

  getConfig: async (smbId: string) => {
    const response = await api.get(`/ai/config/${smbId}`)
    return response.data
  },

  updateSmbSettings: async (smbId: string, payload: Record<string, unknown>) => {
    const response = await api.put(`/ai/settings/smb/${smbId}`, payload)
    return response.data
  },

  updateAgencySettings: async (agencyId: string, payload: Record<string, unknown>) => {
    const response = await api.put(`/ai/settings/agency/${agencyId}`, payload)
    return response.data
  },

  upsertCredential: async (payload: Record<string, unknown>) => {
    const response = await api.post('/ai/credentials', payload)
    return response.data
  },

  getUsage: async (params?: { provider?: string; smb_id?: string; limit?: number }) => {
    const response = await api.get('/ai/usage', { params })
    return response.data
  },
}

// Agency AI Config API
export interface AgencyAIConfig {
  provider: string | null
  model: string | null
  scope: 'agency'
  has_credentials: boolean
}

export interface AgencySummary {
  id: string
  name: string | null
  display_name: string | null
}

interface AgencyListResponse {
  agencies: AgencySummary[]
  count: number
}

export const agencyApi = {
  listAgencies: async (params?: { q?: string; limit?: number }): Promise<AgencySummary[]> => {
    const response = await api.get('/agencies', { params })
    const data = response.data

    // Normalize to array
    if (Array.isArray(data)) {
      return data
    }

    if (data && Array.isArray((data as AgencyListResponse).agencies)) {
      return (data as AgencyListResponse).agencies
    }

    console.warn('[agencyApi] Unexpected response shape from /agencies:', typeof data)
    return []
  },

  getAIConfigById: async (agencyId: string): Promise<AgencyAIConfig> => {
    const response = await api.get(`/agencies/${agencyId}/ai-config`)
    return response.data
  },

  updateAIConfigById: async (
    agencyId: string,
    payload: { provider?: 'gemini'; model?: string; api_key?: string }
  ): Promise<AgencyAIConfig> => {
    const response = await api.patch(`/agencies/${agencyId}/ai-config`, payload)
    return response.data
  },
}

// Business Data API
export const businessDataApi = {
  getSnapshot: async (smbId: string) => {
    const response = await api.get(`/business-data/${smbId}/snapshot`)
    return response.data
  },

  createService: async (smbId: string, payload: Record<string, unknown>) => {
    const response = await api.post(`/business-data/${smbId}/services`, payload)
    return response.data
  },

  updateService: async (smbId: string, serviceId: string, payload: Record<string, unknown>) => {
    const response = await api.put(`/business-data/${smbId}/services/${serviceId}`, payload)
    return response.data
  },

  deleteService: async (smbId: string, serviceId: string) => {
    const response = await api.delete(`/business-data/${smbId}/services/${serviceId}`)
    return response.data
  },

  // Content blocks
  createContentBlock: async (smbId: string, payload: Record<string, unknown>) => {
    const response = await api.post(`/business-data/${smbId}/content-blocks`, payload)
    return response.data
  },

  updateContentBlock: async (smbId: string, blockId: string, payload: Record<string, unknown>) => {
    const response = await api.put(`/business-data/${smbId}/content-blocks/${blockId}`, payload)
    return response.data
  },

  deleteContentBlock: async (smbId: string, blockId: string) => {
    const response = await api.delete(`/business-data/${smbId}/content-blocks/${blockId}`)
    return response.data
  },

  // FAQs
  createFaq: async (smbId: string, payload: Record<string, unknown>) => {
    const response = await api.post(`/business-data/${smbId}/faqs`, payload)
    return response.data
  },

  updateFaq: async (smbId: string, faqId: string, payload: Record<string, unknown>) => {
    const response = await api.put(`/business-data/${smbId}/faqs/${faqId}`, payload)
    return response.data
  },

  deleteFaq: async (smbId: string, faqId: string) => {
    const response = await api.delete(`/business-data/${smbId}/faqs/${faqId}`)
    return response.data
  },

  // Search functions
  searchContent: async (smbId: string, query: string, limit: number = 5) => {
    const response = await api.get(`/content-search/${smbId}/search`, {
      params: { query, limit }
    })
    return response.data
  },

  getContentStats: async (smbId: string) => {
    const response = await api.get(`/content-search/${smbId}/stats`)
    return response.data
  },

  reindexContent: async (smbId: string) => {
    const response = await api.post(`/content-search/${smbId}/reindex`)
    return response.data
  },
}

// Media API
export const mediaApi = {
  uploadMedia: async (file: File, smbId: string, onUploadProgress?: (progressEvent: any) => void) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post(
      `/media/upload?smb_id=${smbId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      }
    )
    return response.data
  },
}

// Templates API
export const templatesApi = {
  getTemplates: async (params?: {
    smb_id: string
    status?: string[]
    search?: string
    category?: string
    limit?: number
    offset?: number
  }) => {
    const { status, ...rest } = params || {};
    const apiParams = {
      ...rest,
      status: status?.join(',')
    };
    const response = await api.get('/templates', { params: apiParams })
    return response.data
  },

  getTemplateVariables: async (templateId: string) => {
    const response = await api.get(`/templates/${templateId}/variables`)
    return response.data
  },

  createTemplate: async (smbId: string, templateData: {
    name: string
    language: string
    category: string
    body_text?: string
    template_status: string
    header_type?: string
    media_id?: string
    template_inputs?: Array<{ parameter_name: string, label: string, placeholder?: string }>
    platform_template_id?: string
    components?: TemplateComponent[]
  }) => {
    const response = await api.post(`/templates?smb_id=${smbId}`, templateData)
    return response.data
  },

  getMetaTemplates: async (smbId: string) => {
    const response = await api.get('/templates/meta', { params: { smb_id: smbId } })
    return response.data  // { templates: [...] }
  },

  verifyTemplateStatus: async (smbId: string, platformTemplateIds: string[]) => {
    const response = await api.post(
      `/templates/verify-status?smb_id=${smbId}`,
      { platform_template_ids: platformTemplateIds }
    )
    return response.data as Record<string, { can_proceed: boolean; status: string; message: string; last_synced_at: string | null }>
  },
}

// Credits API
export interface CreditBalance {
  entity_type: string
  entity_id: string
  total_available: number
  monthly_total: number
  topup_total: number
  free_total: number
  breakdown: {
    monthly_current: number
    monthly_rollover: number
  }
}

export const creditsApi = {
  getCredits: async (entityType: string, entityId: string): Promise<CreditBalance> => {
    try {
      const response = await api.get(`/credits/${entityType}/${entityId}`)
      return response.data
    } catch (error) {
      // Fallback to mock data for development if API is not yet ready or fails
      console.warn('Credits API failed, returning mock data', error)
      return {
        entity_type: entityType,
        entity_id: entityId,
        total_available: 150,
        monthly_total: 80,
        topup_total: 50,
        free_total: 20,
        breakdown: {
          monthly_current: 60,
          monthly_rollover: 20
        }
      }
    }
  }
}

// WhatsApp API
export const whatsappApi = {
  getStatus: async (smbId: string) => {
    const response = await api.get(`/smbs/${smbId}/whatsapp-status`)
    return response.data
  },

  getCredentials: async (smbId: string) => {
    const response = await api.get(`/smbs/${smbId}/whatsapp-credentials`)
    return response.data
  },

  activate: async (smbId: string, phoneNumberId?: string) => {
    const response = await api.post(`/smbs/${smbId}/whatsapp-credentials/activate`, {
      phone_number_id: phoneNumberId
    })
    return response.data
  },

  verify: async (smbId: string) => {
    const response = await api.post(`/smbs/${smbId}/whatsapp-credentials/verify`)
    return response.data
  },

  testConnection: async (smbId: string, data: any) => {
    const response = await api.post(`/smbs/${smbId}/whatsapp-credentials/test-connection`, data)
    return response.data
  },

  saveCredentials: async (smbId: string, data: any, method: 'POST' | 'PUT' = 'POST') => {
    const response = await api({
      url: `/smbs/${smbId}/whatsapp-credentials`,
      method,
      data
    })
    return response.data
  }
}

// Shopify Integration API
export const shopifyApi = {
  install: async (smbId: string, storeDomain: string): Promise<{ authorize_url: string }> => {
    const response = await api.get('/integrations/shopify/install', {
      params: { smb_id: smbId, store_domain: storeDomain },
    })
    return response.data
  },
  getProductsSyncStatus: async (smbId: string): Promise<{ status: string, store_domain: string | null }> => {
    const response = await api.get('/integrations/shopify/products-sync-status', {
      params: { smb_id: smbId }
    })
    return response.data
  }
}

// Notifications API
export const notificationsApi = {
  subscribe: async (token: string, deviceInfo?: string) => {
    const response = await api.post('/notifications/subscribe', {
      token,
      device_info: deviceInfo
    })
    return response.data
  },

  unsubscribe: async (token: string) => {
    const response = await api.delete('/notifications/unsubscribe', {
      data: { token }
    })
    return response.data
  }
}

// Payments & Billing API
export * from './paymentsApi'
export { default as paymentsApi } from './paymentsApi'

export default api

// Export campaignApi for convenience
export { default as campaignApi } from './campaignApi'

